import { useCallback } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { LockFill } from '@lyearn/icons';
import { useSnackbar } from '@lyearn/ui';
import { isAccessible as checkAccess, useAccessControl } from '@/components/AccessControl';
import { useUserContext } from '@/modules/auth/hooks/useUser';
import { WorkspaceActions, WorkspaceSubjects } from '@/types/schema';

const useWorkspaceAccessControl = (action?: WorkspaceActions, subject?: WorkspaceSubjects) => {
  const { t } = useTranslation();
  const { showSnackbar } = useSnackbar();
  const { workspacePermissions } = useUserContext();

  const canAccess = useAccessControl(workspacePermissions, action, subject);

  const isAccessible = useCallback(
    (workspaceAction: WorkspaceActions, workspaceSubject: WorkspaceSubjects) =>
      checkAccess(workspacePermissions, workspaceAction, workspaceSubject),
    [workspacePermissions],
  );

  const showPermissionSnackbar = useCallback(
    (actionVerb: string, resource: string) => {
      showSnackbar({
        message: t('Common:YouDontHavePermission', { action: actionVerb, subject: resource }),
        startIcon: <LockFill className="h-20 w-20" fillColorToken="icon-placeholder" />,
      });
    },
    [showSnackbar, t],
  );

  return {
    canAccess,
    isAccessible,
    showPermissionSnackbar,
  };
};

export type UseWorkspaceAccessControlReturnType = ReturnType<typeof useWorkspaceAccessControl>;
export default useWorkspaceAccessControl;
